import PropTypes from "prop-types";
import Tooltip from "@mui/material/Tooltip";
import ReceiptIcon from "../../../assets/images/icons/catering-icons/receipt-icon.svg";
import ZapIcon from "../../../assets/images/icons/catering-icons/zap-icon.svg";
import GiftIcon from "../../../assets/images/icons/catering-icons/gift-icon.svg";
import { Link } from "react-router-dom";
import { ToolTipUI } from "../../Widgets/ToolTipUI";
import formatNumberWithCommas from "../../../core/utils/formatNumberWithCommas";

export function StatCard({
  pointsEarned,
  dollarAmountEarned,
  pointsRedeemed,
  dollarAmountRedeemed,
  pointsExpiring,
  dollarAmountExpiring,
}) {
  const hasExpiringPoints = pointsExpiring && pointsExpiring !== "0 points";

  return (
    <div
      className="bg-white rounded-xl p-4 w-full max-w-md shadow-sm flex flex-col"
      style={{ minHeight: "196px", height: "100%" }}
    >
      <div className="flex items-center mb-4 gap-3">
        <img className="w-[45px] h-[45px]" src={ZapIcon} />
        <span className="text-gray-800 font-medium text-base">Total Points Earned</span>
      </div>

      <div className={hasExpiringPoints ? "mb-3" : "mb-[14px]"}>
        <h2 className="text-2xl font-semibold text-gray-900">
          {pointsEarned} <span className="text-[#475467]">{dollarAmountEarned}</span>
        </h2>
      </div>

      <div className={hasExpiringPoints ? "mb-2" : ""}>
        <p className="text-[#535862] text-sm mb-1">Total Points Redeemed</p>
        <p className="text-[#079455] text-sm font-medium">
          {pointsRedeemed} {dollarAmountRedeemed}
        </p>
      </div>

      {hasExpiringPoints && (
        <div>
          <p className="text-[#535862] text-sm mb-1">Points Expiring in Next 30 Days</p>
          <p className="text-[#FB972C] text-sm font-medium">
            {pointsExpiring} {dollarAmountExpiring}
          </p>
        </div>
      )}
    </div>
  );
}

StatCard.propTypes = {
  pointsEarned: PropTypes.any,
  dollarAmountEarned: PropTypes.string,
  pointsRedeemed: PropTypes.string,
  dollarAmountRedeemed: PropTypes.string,
  pointsExpiring: PropTypes.string,
  dollarAmountExpiring: PropTypes.string,
};

export function getRedeemDisabledReason({ points, minPoints, pendingAmountCents, conversion }) {
  if (pendingAmountCents > 0) return "";

  if (minPoints > 0 && points < minPoints) {
    return `Minimum redemption is ${formatNumberWithCommas(minPoints)} points (~$${formatNumberWithCommas(
      minPoints / conversion
    )}). You need ${formatNumberWithCommas(minPoints - points)} more points.`;
  }

  return "";
}

export function StatCardWithButton({
  onClick,
  subText,
  disabled,
  bottomText,
  btnText = "Upload Receipt",
  header = "Upload Receipts",
  icon,
  daysLeftToRedeem,
  hoursLeftToRedeem,
  disabledReason = "",
  tooltipMessage = "Claim your points before they expire — expired points can't be recovered.",
}) {
  const handleClick = (event) => {
    if (disabled) {
      event.preventDefault();
      return;
    }
    onClick(event);
  };

  return (
    <div
      className="bg-white rounded-xl w-full max-w-md shadow-sm flex flex-col"
      style={{ minHeight: "196px", height: "100%" }}
    >
      <div className="flex gap-3 items-center mb-4 px-4 pt-4">
        <img className="w-[45px] h-[45px]" src={icon ? GiftIcon : ReceiptIcon} />
        <span className="text-gray-800 font-medium text-base">{header}</span>
      </div>

      <div className="px-4 mb-4 flex-grow">
        {subText ? (
          subText
        ) : (
          <p className="text-[#535862] text-sm font-medium">
            Upload receipts to get started with rewards
          </p>
        )}
      </div>

      <div
        className={`border-t border-gray-100 flex ${
          bottomText ||
          (daysLeftToRedeem !== undefined && daysLeftToRedeem !== null && daysLeftToRedeem > 0) ||
          (hoursLeftToRedeem !== undefined && hoursLeftToRedeem !== null && hoursLeftToRedeem > 0)
            ? "justify-between"
            : "justify-end"
        }  p-4 items-center mt-auto`}
      >
        {bottomText ? (
          <div
            style={{
              fontSize: "0.9rem",
              fontStyle: 500,
            }}
          >
            Pending request:
            <span className="font-semibold"> ${bottomText}</span>
          </div>
        ) : daysLeftToRedeem !== undefined && daysLeftToRedeem !== null && daysLeftToRedeem > 0 ? (
          <div className="flex items-center gap-1">
            <span style={{ color: "#BB4D00", fontSize: "14px", fontWeight: 500 }}>
              {daysLeftToRedeem} {daysLeftToRedeem === 1 ? 'day' : 'days'} left to redeem
            </span>
            <ToolTipUI tooltipText={tooltipMessage} />
          </div>
        ) : hoursLeftToRedeem !== undefined && hoursLeftToRedeem !== null && hoursLeftToRedeem > 0 ? (
          <div className="flex items-center gap-1">
            <span style={{ color: "#BB4D00", fontSize: "14px", fontWeight: 500 }}>
              {hoursLeftToRedeem} {hoursLeftToRedeem === 1 ? 'hour' : 'hours'} left to redeem
            </span>
            <ToolTipUI tooltipText={tooltipMessage} />
          </div>
        ) : null}

        {onClick ? (
          <Tooltip
            title={disabled ? disabledReason : ""}
            describeChild
            placement="top"
            enterTouchDelay={0}
            leaveTouchDelay={5000}
          >
            <button
              type="button"
              aria-disabled={disabled ? true : undefined}
              onClick={handleClick}
              className={`bg-[#238856] rounded-lg outline-none border-none p-[8px_12px] flex items-center justify-center text-sm font-semibold text-white m-0 focus-visible:ring-2 focus-visible:ring-[#238856]/40 focus-visible:ring-offset-2 ${
                disabled ? "opacity-70 cursor-not-allowed" : "hover:bg-[#238856]/90"
              }`}
              style={{ textTransform: "none", height: 36 }}
            >
              {btnText}
            </button>
          </Tooltip>
        ) : (
          <Link
            to="/upload-receipts"
            disabled={disabled}
            onClick={onClick}
            className="bg-[#238856] disabled:opacity-70 hover:bg-[#238856]/90 rounded-lg outline-none border-none p-[8px_12px] flex items-center justify-center text-sm font-semibold text-white m-0"
            style={{ textTransform: "none", height: 36 }}
          >
            {btnText}
          </Link>
        )}
      </div>
    </div>
  );
}

StatCardWithButton.propTypes = {
  subText: PropTypes.any,
  onClick: PropTypes.any,
  disabled: PropTypes.bool,
  btnText: PropTypes.string,
  header: PropTypes.string,
  icon: PropTypes.bool,
  bottomText: PropTypes.any,
  daysLeftToRedeem: PropTypes.number,
  hoursLeftToRedeem: PropTypes.number,
  disabledReason: PropTypes.string,
  tooltipMessage: PropTypes.string,
};
